"use client"

import { DateRangePicker, Toggle, Text, Button } from "@/elements"
import useOutsideClick from "@/hooks/useOutsideClick"
import { useFormContext } from "react-hook-form"
import { PostAJobModalFormValues } from "./types"
import { ampli } from "@/ampli"
import adjustDateForTimezone from "@/utils/dates"
import { decodeGraphqlNodeId } from "@/tracking"

interface Props {
  setShowDatePicker: (show: boolean) => void
  jobPostId: string
}

const id = "jobs-date-picker"

export const DatePicker: React.FC<Props> = ({ setShowDatePicker, jobPostId }) => {
  const { setValue, watch } = useFormContext<PostAJobModalFormValues>()
  useOutsideClick(id, () => {
    setShowDatePicker(false)
  })

  const startDate = watch("startDate")
  const endDate = watch("endDate")
  const datesFlexible = watch("datesFlexible")

  return (
    <div
      id={id}
      className="mb-4 w-screen overflow-hidden rounded-md border border-bg-3 bg-bg-1 px-4 py-6 shadow-[0px_8px_25px_0px_rgba(0,0,0,0.2)] sm:w-[390px]">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        minDate={new Date()}
        onChange={(dates: Date[]) => {
          const [start, end] = dates
          ampli.postAJobSetDetail({
            job_id: decodeGraphqlNodeId(jobPostId).numericalId || "",
            job_post_field: "date",
            job_post_field_value: `${start?.toISOString()} - ${end?.toISOString()}`,
          })
          if (start && end) {
            if (start && start > end) {
              setValue("endDate", undefined)
              setValue("startDate", adjustDateForTimezone(end.toISOString()))
            } else {
              setValue("startDate", adjustDateForTimezone(start.toISOString()))
              setValue("endDate", adjustDateForTimezone(end.toISOString()))
            }
          } else {
            if (start) {
              setValue("startDate", adjustDateForTimezone(start.toISOString()))
            }
            if (end) {
              setValue("endDate", adjustDateForTimezone(end.toISOString()))
            }
          }
        }}
      />
      <div className="mt-2 flex flex-row items-center justify-between px-4">
        <div className="flex flex-row items-center">
          <Toggle
            checked={datesFlexible}
            onChange={() => {
              ampli.postAJobSetDetail({
                job_id: decodeGraphqlNodeId(jobPostId).numericalId || "",
                job_post_field: "datesFlexible",
                job_post_field_value: datesFlexible ? "true" : "false",
              })
              setValue("datesFlexible", !datesFlexible)
            }}
          />
          <Text className="ml-2" variant="label">
            Dates are flexible
          </Text>
        </div>
        <div className="flex flex-row items-center gap-1">
          <Button
            variant="white"
            size="small"
            onClick={() => {
              setValue("startDate", undefined)
              setValue("endDate", undefined)
              setValue("datesFlexible", false)
            }}>
            Clear
          </Button>
          <Button size="small" onClick={() => setShowDatePicker(false)}>
            {startDate || endDate || datesFlexible ? "Done" : "Close"}
          </Button>
        </div>
      </div>
    </div>
  )
}
